import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faHourglassHalf,
  faSkullCrossbones,
  faStethoscope,
  faFileExcel,
  faFilePdf,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";

const ServiceRevenueReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  )
    .toISOString()
    .slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const res = await axios.get(
        `${config.smsUrl}/creport/generate_service_revenue_report_new`,
        { params }
      );
      setReport(res.data);
    } catch (err) {
      console.error("Report error:", err);
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.start_date, filters.end_date]);

  const {
    summary = {},
    top_performing = [],
    medium_performing = [],
    dormant_services = [],
    all_services = [],
    generated_at,
    period
  } = report || {};

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB") : "-";

  const formatSSP = (amount) =>
    `SSP ${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0
    })}`;

  const formatUSD = (amount) =>
    `$${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const currentData =
    activeTab === "top"
      ? top_performing
      : activeTab === "medium"
      ? medium_performing
      : activeTab === "dormant"
      ? dormant_services
      : all_services;

  return (
    <>
      <PageTitle title="Service Revenue Report" />
      <SubHeader title="Reports" subTitle="Performance • Revenue • Activity">
        <FontAwesomeIcon icon={faStethoscope} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar />

        {/* ===== TOP SECTION ===== */}
        <div style={{ padding: "8px 0 0 0" }}>
          {/* Back + Export */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "12px",
              gap: "8px"
            }}
          >
            <BackButton to="/reports/generalreports" text="Reports" />

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                Excel
              </button>
              <button
                type="button"
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                PDF
              </button>
            </div>
          </div>

          {/* Date filters – tight */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "10px",
              marginBottom: "14px"
            }}
          >
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.start_date}
              onChange={(e) =>
                setFilters({ ...filters, start_date: e.target.value })
              }
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.end_date}
              onChange={(e) =>
                setFilters({ ...filters, end_date: e.target.value })
              }
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <button
              type="button"
              onClick={fetchReport}
              disabled={loading}
              className="btn btn-sm text-white"
              style={{
                backgroundColor: "#389081",
                borderColor: "#389081",
                height: "32px",
                padding: "0 14px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Summary Cards – system colors */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #389081, #027a5f)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon icon={faRocket} className="mb-1 opacity-75" />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Top Performing
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.4rem" }}>
                  {summary.top_count || 0}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #08819c, #0a6b7a)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faHourglassHalf}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Medium
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.4rem" }}>
                  {summary.medium_count || 0}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #F76B1D, #e05a0d)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faSkullCrossbones}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Dormant
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.4rem" }}>
                  {summary.dormant_count || 0}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #3e3e3e, #2b2a2a)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faChartLine}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Total Revenue
                </h6>
                <div style={{ fontSize: "13px" }}>
                  {formatSSP(summary.total_revenue_ssp)}
                </div>
                <div style={{ fontSize: "13px", fontWeight: 600 }}>
                  {formatUSD(summary.total_revenue_usd)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          {[
            { key: "all", label: `All Services (${all_services.length})` },
            { key: "top", label: `Top (${top_performing.length})` },
            { key: "medium", label: `Medium (${medium_performing.length})` },
            { key: "dormant", label: `Dormant (${dormant_services.length})` }
          ].map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${
                  activeTab === tab.key ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
                style={{ fontSize: "13px" }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* TABLE */}
        {loading ? (
          <div className="text-center py-5">
            <LoadingGif />
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <h5>No services found</h5>
            <p className="small">Try a different date range.</p>
          </div>
        ) : (
          <div
            className="table-responsive bg-white"
            style={{
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid #e0e0e0"
            }}
          >
            <table className="table table-hover align-middle mb-0">
              <thead style={{ backgroundColor: "#e8f5f3" }}>
                <tr>
                  <th style={{ fontSize: "13px", fontWeight: 600 }}>#</th>
                  <th style={{ fontSize: "13px", fontWeight: 600 }}>Service</th>
                  <th style={{ fontSize: "13px", fontWeight: 600 }}>
                    Department
                  </th>
                  <th
                    className="text-center"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    Times
                  </th>
                  <th
                    className="text-center"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    Qty
                  </th>
                  <th
                    className="text-end"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    SSP
                  </th>
                  <th
                    className="text-end"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    USD
                  </th>
                  <th style={{ fontSize: "13px", fontWeight: 600 }}>
                    Last Performed
                  </th>
                  <th
                    className="text-center"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    Days Inactive
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: "13px" }}>{i + 1}</td>
                    <td style={{ fontSize: "13px", fontWeight: 600 }}>
                      {item.service_name}
                    </td>
                    <td style={{ fontSize: "13px" }} className="text-muted">
                      {item.department || "—"}
                    </td>
                    <td className="text-center" style={{ fontSize: "13px" }}>
                      {item.times_performed}
                    </td>
                    <td className="text-center" style={{ fontSize: "13px" }}>
                      {item.total_quantity}
                    </td>
                    <td
                      className="text-end"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      {formatSSP(item.revenue_ssp)}
                    </td>
                    <td
                      className="text-end"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      {formatUSD(item.revenue_usd)}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {formatDate(item.last_performed_date)}
                    </td>
                    <td
                      className="text-center"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      {item.days_inactive >= 99999
                        ? "Never"
                        : `${item.days_inactive}d`}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <td
                    colSpan="5"
                    className="text-end"
                    style={{ fontSize: "13px", fontWeight: 600 }}
                  >
                    Grand Total:
                  </td>
                  <td
                    className="text-end"
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  >
                    {formatSSP(summary.total_revenue_ssp)}
                  </td>
                  <td
                    className="text-end"
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  >
                    {formatUSD(summary.total_revenue_usd)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div
          className="text-center mt-3 text-muted"
          style={{ fontSize: "12px" }}
        >
          Generated: {generated_at || "-"} | Period:{" "}
          {period?.start_date || "-"} to {period?.end_date || "-"}
        </div>
      </Container>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(ServiceRevenueReport);